import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Button from "@/components/site/Button";
import RichTextEditor from "@/components/admin/RichTextEditor";
import BlogArticle from "@/components/site/BlogArticle";
import { formatDatumNL } from "@/lib/datum";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAANDEN = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

function fmtKort(iso) {
    if (!iso) return "-";
    const d = new Date(iso);
    return `${d.getDate()} ${MAANDEN[d.getMonth()]} ${d.getFullYear()}, ${d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Amsterdam" })}`;
}

const FILTERS = [
    ["alle", "Alle"],
    ["concept", "Concept"],
    ["gepubliceerd", "Gepubliceerd"],
    ["prullenbak", "Prullenbak"],
];

// ---------------------------------------------------------------------------
// Lijst + filters + prullenbak
// ---------------------------------------------------------------------------
export default function BlogAdmin({ onUnauthorized }) {
    const [view, setView] = useState("list"); // list | editor
    const [editing, setEditing] = useState(null); // post id
    const [posts, setPosts] = useState(null);
    const [filter, setFilter] = useState("alle");
    const [error, setError] = useState("");
    const [toast, setToast] = useState("");
    const [newTitle, setNewTitle] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [creating, setCreating] = useState(false);
    const toastTimer = useRef(null);

    const showToast = (msg) => {
        setToast(msg);
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(""), 4000);
    };

    const load = async (f = filter) => {
        try {
            const r = await axios.get(`${API}/admin/blog/posts`, {
                params: { filter: f },
                withCredentials: true,
            });
            setPosts(r.data.posts);
        } catch (err) {
            if (err.response?.status === 401) return onUnauthorized();
            setError("De artikelen konden niet geladen worden.");
        }
    };

    useEffect(() => {
        setPosts(null);
        load(filter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const create = async () => {
        if (!newTitle.trim()) return;
        setCreating(true);
        setError("");
        try {
            const r = await axios.post(
                `${API}/admin/blog/posts`,
                { title: newTitle.trim() },
                { withCredentials: true }
            );
            setNewTitle("");
            setShowNew(false);
            setEditing(r.data.id);
            setView("editor");
        } catch (err) {
            if (err.response?.status === 401) return onUnauthorized();
            setError(err.response?.data?.detail || "Aanmaken is mislukt.");
        } finally {
            setCreating(false);
        }
    };

    const remove = async (post) => {
        if (!window.confirm(`"${post.title}" naar de prullenbak verplaatsen?`)) return;
        try {
            await axios.delete(`${API}/admin/blog/posts/${post.id}`, { withCredentials: true });
            showToast("Artikel verplaatst naar de prullenbak.");
            load();
        } catch (err) {
            if (err.response?.status === 401) return onUnauthorized();
            setError("Verwijderen is mislukt.");
        }
    };

    const restore = async (post) => {
        try {
            await axios.post(`${API}/admin/blog/posts/${post.id}/restore`, {}, { withCredentials: true });
            showToast("Artikel hersteld.");
            load();
        } catch (err) {
            if (err.response?.status === 401) return onUnauthorized();
            setError("Herstellen is mislukt.");
        }
    };

    if (view === "editor" && editing) {
        return (
            <BlogEditor
                postId={editing}
                onBack={(msg) => {
                    setView("list");
                    setEditing(null);
                    load();
                    if (msg) showToast(msg);
                }}
                onUnauthorized={onUnauthorized}
            />
        );
    }

    return (
        <div data-testid="admin-blog">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
                <div>
                    <h1 className="font-[Fraunces] text-[32px] md:text-[38px] leading-tight" style={{ fontWeight: 300 }}>
                        Blog
                    </h1>
                    <p className="text-[14.5px] text-[var(--hds-ink)]/65 mt-1 max-w-[60ch]">
                        Schrijf en publiceer artikelen. Zolang er niets gepubliceerd is, is de blog onzichtbaar op de site.
                    </p>
                </div>
                <Button variant="primary" icon="arrow" onClick={() => setShowNew(true)} data-testid="blog-new">
                    Nieuw artikel
                </Button>
            </div>

            {error && <p className="mb-5 text-[14px] text-[var(--hds-clay-dark)]" data-testid="blog-error">{error}</p>}

            {showNew && (
                <div className="mb-8 rounded-[14px] border border-[var(--hds-line)] bg-[var(--hds-card)] p-5 md:p-6" data-testid="blog-new-form">
                    <label className="block text-[13px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mb-2">
                        Titel van het nieuwe artikel
                    </label>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <input
                            type="text"
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && create()}
                            data-testid="blog-new-title"
                            className="flex-1 rounded-[10px] bg-[var(--hds-sand)] border border-[var(--hds-line)] focus:border-[var(--hds-clay)] outline-none px-4 py-3 text-[15px]"
                        />
                        <div className="flex gap-3 items-center">
                            <Button variant="primary" onClick={create} disabled={creating || !newTitle.trim()} data-testid="blog-new-create">
                                {creating ? "Aanmaken..." : "Aanmaken"}
                            </Button>
                            <button type="button" onClick={() => setShowNew(false)} className="text-[13px] text-[var(--hds-ink)]/60 underline underline-offset-2">
                                Annuleren
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex gap-2 mb-6 border-b border-[var(--hds-line)] flex-wrap">
                {FILTERS.map(([key, label]) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setFilter(key)}
                        data-testid={`blog-filter-${key}`}
                        className={`px-4 py-2.5 text-[13.5px] border-b-2 -mb-px transition-colors duration-300 ${
                            filter === key
                                ? "border-[var(--hds-clay)] text-[var(--hds-ink)]"
                                : "border-transparent text-[var(--hds-ink)]/50 hover:text-[var(--hds-ink)]"
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {!posts ? (
                <p className="text-[var(--hds-ink)]/50">Laden...</p>
            ) : posts.length === 0 ? (
                <p className="text-[14.5px] text-[var(--hds-ink)]/55" data-testid="blog-empty">
                    {filter === "prullenbak" ? "De prullenbak is leeg." : "Nog geen artikelen."}
                </p>
            ) : (
                <ul className="divide-y divide-[var(--hds-line)] border-y border-[var(--hds-line)]" data-testid="blog-list">
                    {posts.map((p) => (
                        <li key={p.id} className="py-4 px-2 flex items-center justify-between gap-4 flex-wrap" data-testid={`blog-row-${p.id}`}>
                            <div className="min-w-0">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <span className="text-[15.5px] text-[var(--hds-ink)] font-medium">{p.title}</span>
                                    <span
                                        className={`text-[11px] tracking-[0.14em] uppercase rounded-full px-2.5 py-1 ${
                                            p.deleted_at
                                                ? "bg-[var(--hds-clay)]/15 text-[var(--hds-clay-dark)]"
                                                : p.status === "gepubliceerd"
                                                  ? "bg-[var(--hds-green)]/15 text-[var(--hds-green)]"
                                                  : "bg-[var(--hds-line)]/60 text-[var(--hds-ink)]/60"
                                        }`}
                                        data-testid={`blog-status-${p.id}`}
                                    >
                                        {p.deleted_at ? "Prullenbak" : p.status}
                                    </span>
                                </div>
                                <p className="text-[12.5px] text-[var(--hds-ink)]/45 mt-1">
                                    /blog/{p.slug}
                                    {p.published_at ? ` · gepubliceerd ${formatDatumNL(p.published_at)}` : ""}
                                    {` · bewerkt ${fmtKort(p.updated_at)}`}
                                </p>
                            </div>
                            <div className="flex items-center gap-4 shrink-0">
                                {p.deleted_at ? (
                                    <button
                                        type="button"
                                        onClick={() => restore(p)}
                                        data-testid={`blog-restore-${p.id}`}
                                        className="text-[13px] text-[var(--hds-green)] underline underline-offset-2"
                                    >
                                        Herstellen
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setEditing(p.id);
                                                setView("editor");
                                            }}
                                            data-testid={`blog-edit-${p.id}`}
                                            className="text-[13px] text-[var(--hds-clay)] underline underline-offset-2"
                                        >
                                            Bewerken
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => remove(p)}
                                            data-testid={`blog-delete-${p.id}`}
                                            className="text-[13px] text-[var(--hds-ink)]/50 hover:text-[var(--hds-clay-dark)] underline underline-offset-2"
                                        >
                                            Verwijderen
                                        </button>
                                    </>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {toast && (
                <div role="status" data-testid="blog-toast" className="fixed bottom-6 right-6 z-50 rounded-[12px] bg-[var(--hds-green)] text-[var(--hds-card)] px-5 py-3.5 text-[14.5px] shadow-[0_16px_48px_rgba(44,39,34,0.25)]">
                    {toast}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Artikel-editor
// ---------------------------------------------------------------------------
function BlogEditor({ postId, onBack, onUnauthorized }) {
    const [post, setPost] = useState(null);
    const [form, setForm] = useState(null);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState("");
    const [showPreview, setShowPreview] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const toastTimer = useRef(null);

    const showToast = (msg) => {
        setToast(msg);
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(""), 4000);
    };

    useEffect(() => {
        axios
            .get(`${API}/admin/blog/posts/${postId}`, { withCredentials: true })
            .then((r) => {
                setPost(r.data);
                setForm({
                    title: r.data.title,
                    slug: r.data.slug,
                    excerpt: r.data.excerpt || "",
                    body: r.data.body || "",
                    featured_image: r.data.featured_image || "",
                    featured_alt: r.data.featured_alt || "",
                    seo_title: r.data.seo_title || "",
                    seo_description: r.data.seo_description || "",
                });
            })
            .catch((err) => {
                if (err.response?.status === 401) return onUnauthorized();
                setError("Het artikel kon niet geladen worden.");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postId]);

    const setF = (patch) => setForm((f) => ({ ...f, ...patch }));

    const save = async () => {
        setError("");
        if (!form.title.trim()) {
            setError("De titel mag niet leeg zijn.");
            return null;
        }
        if (form.featured_image && !form.featured_alt.trim()) {
            setError("Alt-tekst is verplicht bij een foto.");
            return null;
        }
        setSaving(true);
        try {
            const r = await axios.put(`${API}/admin/blog/posts/${postId}`, form, { withCredentials: true });
            setPost(r.data.post);
            setF({ slug: r.data.post.slug });
            return r.data.post;
        } catch (err) {
            if (err.response?.status === 401) return onUnauthorized();
            setError(err.response?.data?.detail || "Opslaan is mislukt.");
            return null;
        } finally {
            setSaving(false);
        }
    };

    const saveAsConcept = async () => {
        const saved = await save();
        if (saved) showToast("Opgeslagen als concept.");
    };

    const publish = async () => {
        const saved = await save();
        if (!saved) return;
        if (!window.confirm("Dit artikel publiceren? Het wordt zichtbaar op de site.")) return;
        try {
            const r = await axios.post(`${API}/admin/blog/posts/${postId}/publish`, {}, { withCredentials: true });
            setPost(r.data.post);
            showToast("Gepubliceerd. Het artikel staat nu op de site.");
        } catch (err) {
            if (err.response?.status === 401) return onUnauthorized();
            setError(err.response?.data?.detail || "Publiceren is mislukt.");
        }
    };

    const unpublish = async () => {
        if (!window.confirm("Terug naar concept? Het artikel is dan niet meer zichtbaar op de site.")) return;
        try {
            await axios.post(`${API}/admin/blog/posts/${postId}/unpublish`, {}, { withCredentials: true });
            setPost((p) => ({ ...p, status: "concept" }));
            showToast("Teruggezet naar concept.");
        } catch (err) {
            if (err.response?.status === 401) return onUnauthorized();
            setError("Terugzetten is mislukt.");
        }
    };

    const remove = async () => {
        if (!window.confirm("Dit artikel naar de prullenbak verplaatsen?")) return;
        try {
            await axios.delete(`${API}/admin/blog/posts/${postId}`, { withCredentials: true });
            onBack("Artikel verplaatst naar de prullenbak.");
        } catch (err) {
            if (err.response?.status === 401) return onUnauthorized();
            setError("Verwijderen is mislukt.");
        }
    };

    const uploadFeatured = async (file) => {
        setError("");
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
            setError("Bestand is te groot (max 8 MB).");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await axios.post(`${API}/admin/media`, fd, { withCredentials: true });
            setF({ featured_image: r.data.url });
        } catch (err) {
            if (err.response?.status === 401) return onUnauthorized();
            setError(err.response?.data?.detail || "Uploaden is mislukt.");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    if (!post || !form) {
        return <p className="text-[var(--hds-ink)]/50">{error || "Laden..."}</p>;
    }

    const isPublished = post.status === "gepubliceerd";
    const previewPost = { ...post, ...form };

    return (
        <div data-testid="blog-editor">
            <button type="button" onClick={() => onBack()} data-testid="blog-editor-back" className="text-[13px] text-[var(--hds-ink)]/60 hover:text-[var(--hds-clay)] underline underline-offset-2 mb-6">
                Terug naar Blog
            </button>

            <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
                <div>
                    <h1 className="font-[Fraunces] text-[28px] md:text-[34px] leading-tight" style={{ fontWeight: 300 }}>
                        {post.title}
                    </h1>
                    <p className="text-[12.5px] text-[var(--hds-ink)]/50 mt-1">
                        Status: {isPublished ? "gepubliceerd" : "concept"}
                        {isPublished && (
                            <>
                                {" · "}
                                <a href={`/blog/${post.slug}`} target="_blank" rel="noreferrer" className="text-[var(--hds-clay)] underline underline-offset-2" data-testid="blog-view-public">
                                    Bekijk op de site
                                </a>
                            </>
                        )}
                    </p>
                </div>
            </div>

            {error && <p className="mb-5 text-[14px] text-[var(--hds-clay-dark)]" data-testid="blog-editor-error">{error}</p>}

            <div className="space-y-6">
                <div className="rounded-[14px] border border-[var(--hds-line)] bg-[var(--hds-card)] p-5 md:p-6">
                    <label className="block text-[13px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mb-2">Titel</label>
                    <input type="text" value={form.title} onChange={(e) => setF({ title: e.target.value })} data-testid="blog-field-title" className="w-full rounded-[10px] bg-[var(--hds-sand)] border border-[var(--hds-line)] focus:border-[var(--hds-clay)] outline-none px-4 py-3 text-[15px]" />
                    <label className="block text-[13px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mt-5 mb-2">Slug</label>
                    <div className="flex items-center gap-2">
                        <span className="text-[13px] text-[var(--hds-ink)]/45">/blog/</span>
                        <input type="text" value={form.slug} onChange={(e) => setF({ slug: e.target.value })} data-testid="blog-field-slug" className="flex-1 rounded-[10px] bg-[var(--hds-sand)] border border-[var(--hds-line)] focus:border-[var(--hds-clay)] outline-none px-4 py-2.5 text-[14px]" />
                    </div>
                    <div className="flex items-baseline justify-between gap-3 mt-5 mb-2">
                        <label className="text-[13px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55">Samenvatting</label>
                        <span className={`text-[12px] ${form.excerpt.length > 200 ? "text-[var(--hds-clay-dark)] font-semibold" : "text-[var(--hds-ink)]/45"}`} data-testid="blog-excerpt-count">
                            {form.excerpt.length}/200
                        </span>
                    </div>
                    <textarea rows={2} value={form.excerpt} onChange={(e) => setF({ excerpt: e.target.value })} data-testid="blog-field-excerpt" className="w-full rounded-[10px] bg-[var(--hds-sand)] border border-[var(--hds-line)] focus:border-[var(--hds-clay)] outline-none px-4 py-3 text-[14.5px]" />
                </div>

                <div className="rounded-[14px] border border-[var(--hds-line)] bg-[var(--hds-card)] p-5 md:p-6">
                    <label className="block text-[13px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mb-3">Artikeltekst</label>
                    <RichTextEditor full value={form.body} onChange={(v) => setF({ body: v })} testid="blog-field-body" />
                </div>

                <div className="rounded-[14px] border border-[var(--hds-line)] bg-[var(--hds-card)] p-5 md:p-6" data-testid="blog-featured">
                    <label className="block text-[13px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mb-3">Uitgelichte foto</label>
                    <div className="flex flex-col sm:flex-row gap-5">
                        <div className="shrink-0">
                            {form.featured_image ? (
                                <img src={form.featured_image} alt={form.featured_alt || ""} className="w-full max-w-[240px] rounded-[10px] border border-[var(--hds-line)] object-cover" data-testid="blog-featured-preview" />
                            ) : (
                                <div className="w-[240px] h-[140px] rounded-[10px] border border-dashed border-[var(--hds-line)] flex items-center justify-center text-[12.5px] text-[var(--hds-ink)]/40">
                                    Nog geen foto
                                </div>
                            )}
                        </div>
                        <div className="flex-1 space-y-3">
                            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => uploadFeatured(e.target.files?.[0])} className="hidden" data-testid="blog-featured-input" />
                            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="blog-featured-upload" className="inline-flex items-center gap-2 rounded-full border border-[var(--hds-line)] bg-[var(--hds-sand)] px-4 py-2 text-[13.5px] text-[var(--hds-ink)] hover:border-[var(--hds-clay)] transition-colors duration-300 disabled:opacity-50">
                                {uploading ? "Uploaden..." : form.featured_image ? "Andere foto uploaden" : "Foto uploaden"}
                            </button>
                            {form.featured_image && (
                                <>
                                    <label className="block text-[12px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55">Alt-tekst (verplicht)</label>
                                    <input type="text" value={form.featured_alt} onChange={(e) => setF({ featured_alt: e.target.value })} data-testid="blog-featured-alt" className="w-full rounded-[10px] bg-[var(--hds-sand)] border border-[var(--hds-line)] focus:border-[var(--hds-clay)] outline-none px-4 py-2.5 text-[14px]" />
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="rounded-[14px] border border-[var(--hds-line)] bg-[var(--hds-card)]/60 p-5 md:p-6" data-testid="blog-seo">
                    <h2 className="text-[11.5px] tracking-[0.24em] uppercase text-[var(--hds-clay)] mb-4">SEO</h2>
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                        <label className="text-[13px] text-[var(--hds-ink)]/70">SEO-titel (leeg = titel van het artikel)</label>
                        <span className={`text-[12px] ${form.seo_title.length > 60 ? "text-[var(--hds-clay-dark)] font-semibold" : "text-[var(--hds-ink)]/45"}`} data-testid="blog-seo-title-count">{form.seo_title.length}/60</span>
                    </div>
                    <input type="text" value={form.seo_title} onChange={(e) => setF({ seo_title: e.target.value })} data-testid="blog-field-seo-title" className="w-full rounded-[10px] bg-[var(--hds-sand)] border border-[var(--hds-line)] focus:border-[var(--hds-clay)] outline-none px-4 py-2.5 text-[14.5px]" />
                    <div className="flex items-baseline justify-between gap-3 mt-4 mb-2">
                        <label className="text-[13px] text-[var(--hds-ink)]/70">SEO-beschrijving (leeg = samenvatting)</label>
                        <span className={`text-[12px] ${form.seo_description.length > 155 ? "text-[var(--hds-clay-dark)] font-semibold" : "text-[var(--hds-ink)]/45"}`} data-testid="blog-seo-desc-count">{form.seo_description.length}/155</span>
                    </div>
                    <textarea rows={2} value={form.seo_description} onChange={(e) => setF({ seo_description: e.target.value })} data-testid="blog-field-seo-description" className="w-full rounded-[10px] bg-[var(--hds-sand)] border border-[var(--hds-line)] focus:border-[var(--hds-clay)] outline-none px-4 py-2.5 text-[14.5px]" />
                </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3 items-center">
                <Button variant="primary" onClick={saveAsConcept} disabled={saving} data-testid="blog-save-concept">
                    {saving ? "Opslaan..." : isPublished ? "Opslaan" : "Opslaan als concept"}
                </Button>
                <Button variant="secondary" onClick={() => setShowPreview(true)} data-testid="blog-preview-open">
                    Voorbeeld
                </Button>
                {isPublished ? (
                    <Button variant="secondary" onClick={unpublish} data-testid="blog-unpublish">
                        Terug naar concept
                    </Button>
                ) : (
                    <Button variant="secondary" icon="arrow" onClick={publish} disabled={saving} data-testid="blog-publish">
                        Publiceren
                    </Button>
                )}
                <button type="button" onClick={remove} data-testid="blog-delete" className="text-[13px] text-[var(--hds-clay-dark)] underline underline-offset-2 ml-auto">
                    Verwijderen
                </button>
            </div>

            {showPreview && (
                <div className="fixed inset-0 z-[60] bg-[rgba(44,39,34,0.45)] overflow-y-auto" onClick={() => setShowPreview(false)} data-testid="blog-preview-modal">
                    <div className="mx-auto max-w-[880px] my-10 md:my-16 bg-[var(--hds-sand)] rounded-[16px] px-5 md:px-10 py-10 md:py-14" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-6">
                            <p className="text-[11.5px] tracking-[0.24em] uppercase text-[var(--hds-clay)]">Voorbeeld (nog niet zichtbaar op de site)</p>
                            <button type="button" onClick={() => setShowPreview(false)} data-testid="blog-preview-close" className="text-[13px] text-[var(--hds-ink)]/60 underline underline-offset-2">
                                Sluiten
                            </button>
                        </div>
                        <BlogArticle post={previewPost} preview />
                    </div>
                </div>
            )}

            {toast && (
                <div role="status" data-testid="blog-toast" className="fixed bottom-6 right-6 z-50 rounded-[12px] bg-[var(--hds-green)] text-[var(--hds-card)] px-5 py-3.5 text-[14.5px] shadow-[0_16px_48px_rgba(44,39,34,0.25)]">
                    {toast}
                </div>
            )}
        </div>
    );
}
